/**
 * Title-case a value for DISPLAY only.
 *
 * Names arrive however they were typed — "guntur", "SRIKAKULAM", "ap director
 * general of police" — and a list that mixes all three reads as three different
 * conventions rather than one set of names. This normalises the rendering; the
 * stored value is never touched, so searching, sorting and exports still see
 * exactly what was saved.
 *
 * Deliberately conservative:
 *  · a token that is ALL CAPS and short is left alone (GRP, CCS, SDPO, NTR) —
 *    those are abbreviations, and "Grp Guntakal" would be wrong;
 *  · a token carrying its own inner capital is left alone (McBride, iPhone);
 *  · separators inside a token are preserved, so "sub-division" becomes
 *    "Sub-Division" rather than "Sub-division";
 *  · the small words a title keeps lowercase stay lowercase unless they lead,
 *    so "AP Director General of Police" is not mangled into "... Of Police".
 */
const ACRONYM = /^[A-Z0-9&().-]+$/;

const SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on',
  'or', 'the', 'to', 'via', 'with',
]);

const capitalizePart = (part: string): string =>
  part ? part.charAt(0).toUpperCase() + part.slice(1) : part;

const titleCaseToken = (token: string): string => {
  if (!token) return token;
  // Abbreviations and anything already deliberately cased stay as they are.
  if (token.length <= 5 && ACRONYM.test(token)) return token;
  if (/[a-z][A-Z]/.test(token)) return token;
  return token
    .toLowerCase()
    .split(/([-/.'])/)
    .map((part, i) => (i % 2 === 1 ? part : capitalizePart(part)))
    .join('');
};

export const titleCase = (value?: string | null): string =>
  (value ?? '')
    .trim()
    .split(/(\s+)/)
    .map((token, i) => {
      if (i % 2 === 1) return token;                       // the whitespace itself
      if (i > 0 && SMALL_WORDS.has(token.toLowerCase())) return token.toLowerCase();
      return titleCaseToken(token);
    })
    .join('');

export default titleCase;
